import React, { ReactNode, useMemo, useState } from 'react';

import { FormComponent, UiComponent } from 'components';
import { usePageOption } from 'hooks';
import { UserQuery } from 'queries';
import {
  IUserGroupCandidate,
  IUserListItem,
  SearchUserFilterType,
} from 'types';
import { formatNameWithPhone } from './format';
import * as PickerStyle from './style';

const COLS: string[] = ['이름', '선택'];

interface Props {
  selectedIds: Set<number>;
  onAdd: (users: IUserGroupCandidate[]) => void;
}

function UserSearch({ selectedIds, onAdd }: Props) {
  const { pageOption, handlePage } = usePageOption(10);
  const [filter, setFilter] = useState<SearchUserFilterType>({});

  const { data } = UserQuery.useGetAll({
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const handleSearch = (d: SearchUserFilterType) => {
    setFilter({ name: d.name });
    handlePage({ ...pageOption, page: 1 });
  };

  const renderer: Record<
    (typeof COLS)[number],
    (data: IUserListItem & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      이름: ({ name, phone }: IUserListItem) => (
        <>{formatNameWithPhone(name, phone)}</>
      ),
      선택: ({ id, name, phone }: IUserListItem) => {
        const isSelected = selectedIds.has(id);
        return (
          <UiComponent.Button
            variant={isSelected ? 'border' : 'contained'}
            color="primary"
            disabled={isSelected}
            onClick={() => onAdd([{ id, name, phone }])}
          >
            {isSelected ? '선택됨' : '선택'}
          </UiComponent.Button>
        );
      },
    }),
    [selectedIds, onAdd],
  );

  const rows = (data?.data || []).map((u: IUserListItem) => ({
    ...u,
    rowKey: u.id,
  }));

  return (
    <>
      <FormComponent.Form css={PickerStyle.searchRow} submit={handleSearch}>
        <FormComponent.SearchInput
          name="name"
          placeholder="이름으로 검색해주세요"
          css={{ width: '100%' }}
        />
      </FormComponent.Form>
      <div css={PickerStyle.listWrap}>
        <UiComponent.Table
          cols={COLS}
          renderer={renderer}
          data={rows}
          sticky
          empty="검색 결과가 없습니다"
        />
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
    </>
  );
}

export default UserSearch;
